import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Users, Copy, Trash2, ArrowRight, Loader2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function StudyRooms() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [form, setForm] = useState({ name: "", sc_id: "", course_id: "" });
  const [studies, setStudies] = useState([]);
  const [courses, setCourses] = useState([]);

  const isPlusOrPro = user?.plan === "plus" || user?.plan === "pro";

  useEffect(() => {
    if (!isPlusOrPro) return;
    load();
    api.get("/subcourses").then(r => setStudies(r.data.filter(s => s.generation_status === "ready"))).catch(() => {});
    api.get("/courses").then(r => setCourses(r.data.courses || [])).catch(() => {});
  }, [isPlusOrPro]);

  const load = async () => {
    try {
      const r = await api.get("/rooms");
      setRooms(r.data.rooms || []);
    } catch { toast.error("Couldn't load rooms"); }
    finally { setLoading(false); }
  };

  const createRoom = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!form.sc_id && !form.course_id) { toast.error("Select a study or course."); return; }
    setCreating(true);
    try {
      const payload = { name: form.name };
      if (form.sc_id) payload.sc_id = form.sc_id;
      if (form.course_id) payload.course_id = form.course_id;
      const r = await api.post("/rooms", payload);
      setShowCreate(false);
      setForm({ name: "", sc_id: "", course_id: "" });
      toast.success(`Room created — code: ${r.data.code}`);
      // Take the host straight into the new room session.
      nav(`/app/rooms/${r.data.code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create room");
    } finally { setCreating(false); }
  };

  const deleteRoom = async (id) => {
    if (!confirm("End this room? All members will be disconnected.")) return;
    try {
      await api.delete(`/rooms/${id}`);
      setRooms(prev => prev.filter(r => r.id !== id));
      toast.success("Room ended");
    } catch { toast.error("Couldn't end room"); }
  };

  const joinRoom = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setJoining(true);
    try {
      await api.get(`/rooms/join/${code}`);
      nav(`/app/rooms/${code}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Room not found");
    } finally { setJoining(false); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code ${code} copied!`);
  };

  if (!isPlusOrPro) return (
    <div className="max-w-[760px] mx-auto px-5 py-12">
      <div className="rounded-2xl p-8 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
        <Users size={32} className="mx-auto mb-4 text-[#B8A4FF]" />
        <h2 className="text-[22px] font-semibold mb-2">Collaborative Rooms</h2>
        <p className="text-[#B0AEBC] text-[15px] mb-6">Study together in real-time with classmates. Available on Plus and Pro plans.</p>
        <Link to="/app/billing" className="btn btn-primary px-8 py-3">Upgrade to Plus</Link>
      </div>
    </div>
  );

  return (
    <div className="max-w-[900px] mx-auto px-5 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-[28px] font-bold">Study Rooms</h1>
          <p className="text-[14px] text-[#B0AEBC] mt-1">Study live with classmates — real-time shared flashcard sessions.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-room-btn"
          className="btn btn-primary flex items-center gap-2 px-4 py-2.5">
          <Plus size={15} /> New Room
        </button>
      </div>

      {/* Join room */}
      <div className="rounded-2xl p-4 mb-6 flex items-center gap-3"
        style={{ background: "rgba(184,164,255,0.06)", border: "1px solid rgba(184,164,255,0.2)" }}>
        <input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())}
          className="input flex-1 text-[15px] tracking-widest font-mono uppercase"
          placeholder="ENTER ROOM CODE" maxLength={6} data-testid="join-code-input" />
        <button onClick={joinRoom} disabled={joining || joinCode.length < 6}
          className="btn btn-primary px-5 py-2.5 flex items-center gap-2 flex-shrink-0" data-testid="join-room-btn">
          {joining ? <Loader2 size={15} className="animate-spin" /> : <><ArrowRight size={15} /> Join</>}
        </button>
      </div>

      {/* Create room modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-5"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)} />
            <motion.div className="relative w-full max-w-[440px] rounded-2xl p-6 z-10"
              style={{ background: "#111113", border: "1px solid rgba(255,255,255,0.1)" }}
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}>
              <h2 className="text-[18px] font-semibold mb-5">Create a Room</h2>
              <form onSubmit={createRoom} className="space-y-4">
                <div>
                  <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Room name</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="input" placeholder="e.g. Physics exam prep" required data-testid="room-name-input" />
                </div>
                <div>
                  <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Study (optional)</label>
                  <select value={form.sc_id} onChange={e => setForm(f => ({ ...f, sc_id: e.target.value, course_id: "" }))}
                    className="input" data-testid="room-study-select">
                    <option value="">— Select a study —</option>
                    {studies.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                  </select>
                </div>
                {!form.sc_id && (
                  <div>
                    <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Or pick a Course</label>
                    <select value={form.course_id} onChange={e => setForm(f => ({ ...f, course_id: e.target.value, sc_id: "" }))}
                      className="input" data-testid="room-course-select">
                      <option value="">— Select a course —</option>
                      {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowCreate(false)} className="btn btn-secondary flex-1 py-3">Cancel</button>
                  <button type="submit" disabled={creating} className="btn btn-primary flex-1 py-3" data-testid="room-create-submit">
                    {creating ? <Loader2 size={15} className="animate-spin mx-auto" /> : "Create room"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* My rooms */}
      <div>
        <h2 className="text-[13px] font-semibold text-[#B0AEBC] uppercase tracking-widest mb-3">My Active Rooms</h2>
        {loading ? (
          <div className="text-center py-12 text-[#555]"><Loader2 size={20} className="animate-spin mx-auto" /></div>
        ) : rooms.length === 0 ? (
          <div className="text-center py-12 text-[#555] text-[14px]">No active rooms. Create one to start studying with friends.</div>
        ) : (
          <div className="space-y-3">
            {rooms.map(room => (
              <div key={room.id} className="rounded-2xl p-4 flex items-center justify-between"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#B8A4FF]/10 border border-[#B8A4FF]/20 flex items-center justify-center">
                    <Users size={16} className="text-[#B8A4FF]" />
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold text-white">{room.name}</p>
                    <button onClick={() => copyCode(room.code)}
                      className="flex items-center gap-1 text-[12px] text-[#555] hover:text-[#B8A4FF] transition-colors mt-0.5">
                      <span className="font-mono tracking-widest">{room.code}</span>
                      <Copy size={11} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={`/app/rooms/${room.code}`}
                    className="btn btn-primary text-[12px] px-3 py-2 flex items-center gap-1.5" data-testid={`open-room-${room.code}`}>
                    <ExternalLink size={12} /> Open
                  </Link>
                  <button onClick={() => deleteRoom(room.id)} className="btn-icon text-[#555] hover:text-red-400" data-testid={`delete-room-${room.id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
